import React, { useCallback, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { FaKeyboard } from "react-icons/fa";

const SPECIAL_KEYS = {
  Backspace: [0xff08, "Backspace"],
  Tab: [0xff09, "Tab"],
  Enter: [0xff0d, "Enter"],
  Escape: [0xff1b, "Escape"],
  ArrowLeft: [0xff51, "ArrowLeft"],
  ArrowUp: [0xff52, "ArrowUp"],
  ArrowRight: [0xff53, "ArrowRight"],
  ArrowDown: [0xff54, "ArrowDown"],
  Delete: [0xffff, "Delete"],
};

const unicodeKeysym = (character) => {
  const codePoint = character.codePointAt(0);
  return codePoint <= 0xff ? codePoint : 0x01000000 | codePoint;
};

export default function RemoteMobileKeyboard({ rfbRef, onRemoteFocus }) {
  const inputRef = useRef(null);
  const composingRef = useRef(false);
  const [active, setActive] = useState(false);

  const sendKey = useCallback((keysym, code = "") => {
    const rfb = rfbRef.current;
    if (!rfb) return;
    onRemoteFocus?.();
    rfb.sendKey(keysym, code, true);
    rfb.sendKey(keysym, code, false);
  }, [onRemoteFocus, rfbRef]);

  const sendText = useCallback((text) => {
    Array.from(String(text || "")).forEach((character) => {
      sendKey(unicodeKeysym(character));
    });
  }, [sendKey]);

  const openKeyboard = useCallback(() => {
    onRemoteFocus?.();
    inputRef.current?.focus({ preventScroll: true });
  }, [onRemoteFocus]);

  const handleInput = useCallback((event) => {
    const nativeEvent = event.nativeEvent || {};
    if (composingRef.current || nativeEvent.isComposing) return;
    const inputType = String(nativeEvent.inputType || "");
    if (inputType.startsWith("delete")) {
      sendKey(...SPECIAL_KEYS.Backspace);
    } else {
      sendText(nativeEvent.data ?? event.currentTarget.value);
    }
    event.currentTarget.value = "";
  }, [sendKey, sendText]);

  const handleKeyDown = useCallback((event) => {
    const special = SPECIAL_KEYS[event.key];
    // Keep noVNC's document-level keyboard listener from sending a second copy.
    event.stopPropagation();
    if (!special) return;
    event.preventDefault();
    sendKey(...special);
  }, [sendKey]);

  return (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        position: "absolute",
        right: 10,
        bottom: 10,
        zIndex: 5,
      }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <input
        ref={inputRef}
        aria-label="Type in remote console"
        autoCapitalize="none"
        autoComplete="off"
        autoCorrect="off"
        enterKeyHint="enter"
        inputMode="text"
        spellCheck={false}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onCompositionStart={() => { composingRef.current = true; }}
        onCompositionEnd={(event) => {
          composingRef.current = false;
          sendText(event.data);
          event.currentTarget.value = "";
        }}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          right: 0,
          bottom: 0,
          opacity: 0.01,
          border: 0,
          padding: 0,
          fontSize: 16,
        }}
      />
      <Tooltip title={active ? "Phone keyboard open" : "Open phone keyboard"}>
        <IconButton
          aria-label={active ? "Phone keyboard open" : "Open phone keyboard"}
          onClick={openKeyboard}
          sx={{
            width: 44,
            height: 44,
            color: "#fff",
            bgcolor: active ? "#2e7d32" : "rgba(20,20,20,0.88)",
            border: "1px solid rgba(255,255,255,0.7)",
            boxShadow: 3,
            "&:hover": { bgcolor: active ? "#1b5e20" : "rgba(20,20,20,0.96)" },
          }}
        >
          <FaKeyboard size={22} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
